type NetworkStateListener = () => void;

interface NetworkState {
  online: boolean;
  previous: boolean | undefined;
}

const DEBOUNCE_MS = 300;

class NetworkStateSingleton {
  private state: NetworkState = { online: true, previous: undefined };
  private listeners = new Set<NetworkStateListener>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;
  private disconnectedAt: number | null = null;
  private lastTrackedOnline: boolean | undefined = undefined;

  init() {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    const initialOnline = navigator.onLine ?? true;
    // Only update state if the value actually changed to avoid creating new object reference
    if (this.state.online !== initialOnline) {
      this.state = { online: initialOnline, previous: undefined };
    }

    window.addEventListener('online', () => this.handleNetworkChange(true));
    window.addEventListener('offline', () => this.handleNetworkChange(false));
  }

  private handleNetworkChange(isOnline: boolean) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      const wasOnline = this.state.online;

      if (wasOnline === isOnline) {
        this.disconnectedAt = null;
        return;
      }

      if (wasOnline && !isOnline) {
        this.disconnectedAt = Date.now();
      }

      this.state = { online: isOnline, previous: wasOnline };
      this.notifyListeners();
    }, DEBOUNCE_MS);
  }

  private notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Network state listener error:', error);
      }
    });
  }

  getState(): NetworkState {
    return this.state;
  }

  isOnline(): boolean {
    return this.state.online;
  }

  getOfflineDuration(): number | null {
    if (this.disconnectedAt === null) return null;
    if (this.state.online) {
      const duration = Date.now() - this.disconnectedAt;
      this.disconnectedAt = null;
      return duration;
    }
    return Date.now() - this.disconnectedAt;
  }

  shouldTrackEvent(currentOnline: boolean): boolean {
    if (this.lastTrackedOnline === currentOnline) return false;
    this.lastTrackedOnline = currentOnline;
    return true;
  }

  subscribe(listener: NetworkStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  waitForOnline(signal?: AbortSignal): Promise<void> {
    if (this.state.online) {
      return Promise.resolve();
    }

    if (signal?.aborted) {
      return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    }

    return new Promise((resolve, reject) => {
      const unsubscribe = this.subscribe(() => {
        if (this.state.online) {
          cleanup();
          resolve();
        }
      });

      const onAbort = () => {
        cleanup();
        reject(signal!.reason ?? new DOMException('Aborted', 'AbortError'));
      };

      const cleanup = () => {
        unsubscribe();
        signal?.removeEventListener('abort', onAbort);
      };

      signal?.addEventListener('abort', onAbort);
    });
  }
}

export const networkState = new NetworkStateSingleton();
